import {
  checkPlacement,
  checkIntraGroupAccessoryCollisions,
} from '@shared-lib/collision';
import { filterCollisions, toSkipChecks } from './constraintFilter';
import { buildOpeningLookup, hydrateRoomOpenings } from './openingSyncUtils';

// Room-object types the bathroom-analyzer pipeline can actually re-place.
// Anything outside this set is silenced here because "Fix all" cannot
// act on it (no mirror/wall-light/switch repositioning in the pipeline).
// Reinstate by wiring additional surfaces later.
const FIX_ALL_ACTIONABLE_TYPES = new Set([
  'toilet',
  'sink',
  'double-sink',
  'lower-cabinet',
  'shower',
  'bathtub',
  'tub',
  'tub-shower',
]);

/** Type-or-category membership (#1535/M8): producers tag e.g. a
 * `vanity-cabinet` with `category:{'lower-cabinet':true}`, and an exact-type
 * test left those invisible — the same blind spot `upper-cabinet` was
 * explicitly added to close. */
function typeOrCategoryIn(obj, set) {
  if (set.has((obj?.type || '').toLowerCase())) return true;
  const cat = obj?.category;
  if (cat && typeof cat === 'object') {
    for (const k of Object.keys(cat)) {
      if (cat[k] && set.has(String(k).toLowerCase())) return true;
    }
  }
  return false;
}

/**
 * Produced by the kitchen, whatever stamped it.
 *
 * The gate named `kitchen-layout` alone, but the post-processing rules stamp
 * **`kitchen-post-processing`** (oblique corner fills, run fillers, run-end
 * pockets, island cabinets — `oblique-junctions.js`, `run-spans.js`,
 * `kitchen.js`). Those carry no `runId`/`bayId`, so nothing else caught them:
 * `ROOM_TYPE_TO_PIPELINE` maps `lower-cabinet` to the bathroom SINK pipeline,
 * and the server belt (`resolveCollisionGroups`) only checks run/bay ownership.
 * Measured 2026-09-19: **97 kitchen boxes in 23 dev rooms** were actionable.
 *
 * Prefix rather than a list, so a new kitchen producer is covered by naming
 * itself consistently instead of by remembering to edit this file.
 */
function isKitchenProduced(obj) {
  const by = obj?.addedBy;
  return typeof by === 'string' && by.startsWith('kitchen-');
}

function isActionable(obj) {
  // Ownership gate (#1532 step 1): a run- or bay-owned body belongs to the
  // cabinet-run engine, and a kitchen-produced piece to the kitchen — handing
  // either to the bathroom pipeline deleted whole kitchen groups. Still
  // REPORTED via `isReportedSource`.
  return (
    typeOrCategoryIn(obj, FIX_ALL_ACTIONABLE_TYPES) &&
    !obj?.runId &&
    !obj?.bayId &&
    !isKitchenProduced(obj)
  );
}

// Kitchen appliances are REPORTED (red highlight + count) but not
// actionable: "Fix all" runs the bathroom pipeline and cannot re-place a
// range or a dishwasher. Before this a dishwasher 4" inside a range was
// invisible in the UI because neither party was an actionable type.
const REPORTED_ONLY_TYPES = new Set([
  // Upper cabinets are reported too (user ruling 2026-08-23): an upper run
  // through a wall jog or another upper was invisible here.
  'upper-cabinet',
  // Every generated / scan-converted kitchen sink (#1535/M8) — the pipeline
  // can't move one, but a fridge buried in it must not be invisible.
  'kitchen-sink',
  'dishwasher',
  'range',
  'stove',
  'cooktop',
  'oven',
  'wall-oven',
  'refrigerator',
  'fridge',
]);

function isReportedSource(obj) {
  // Reporting is identity-based only: ownership removes actionability, never
  // visibility (a colliding island cabinet must still light the pill).
  return (
    typeOrCategoryIn(obj, FIX_ALL_ACTIONABLE_TYPES) ||
    typeOrCategoryIn(obj, REPORTED_ONLY_TYPES)
  );
}

function dedupeCollisions(allCollisions) {
  const byKey = new Map();
  for (const c of allCollisions) {
    const key = `${c.type}:${[c.sourceId, c.target?.id || ''].sort().join('|')}`;
    const prev = byKey.get(key);
    // Prefer the ACTIONABLE twin: the same pair is detected from both
    // sides, and keeping the run-owned side's entry (actionable: false)
    // hid the other party from Fix All too (#1532 step 1).
    if (!prev || (prev.actionable === false && c.actionable !== false)) {
      byKey.set(key, c);
    }
  }
  const deduped = [...byKey.values()];
  return deduped;
}

/**
 * @param {Object} roomData - room data with slim doors/windows/openings
 * @param {Object} [structureData] - parent structure with canonical
 *   `structureDoors / doors` arrays. Needed so the door-swing check
 *   resolves slim room doors to their canonical geometry (without it,
 *   doorType reads empty and the swing check silently skips every door),
 *   and so slim opening copies — including demoed walls (the opening
 *   subtype `category.opening.demoed`) — reach the wall checks with their
 *   real geometry instead of an empty span.
 */
export function detectRoomCollisions(roomData, structureData) {
  if (!roomData?.objects?.length) return [];
  // Resolve slim room-copy openings (door / window / opening, demoed
  // included) to their structure-canonical geometry before any check.
  // No-op when structureData carries no opening arrays (lookup empty).
  const hydrated = hydrateRoomOpenings(roomData, {
    doors: structureData?.structureDoors || structureData?.doors,
    windows: structureData?.structureWindows || structureData?.windows,
    openings: structureData?.structureOpenings || structureData?.openings,
  });
  const rd = {
    walls: roomData.walls || [],
    objects: roomData.objects || [],
    doors: hydrated.doors || [],
    windows: hydrated.windows || [],
    openings: hydrated.openings || [],
    floors: roomData.floors || [],
  };
  const structureDoorsById = buildOpeningLookup({
    doors: structureData?.structureDoors || structureData?.doors,
  });
  const constraints = roomData.disabledConstraints || [];
  // A bay is one assembly: the sink SITS in its sink-base, the countertop
  // caps both. Reporting kitchen sinks (#1535/M8) made those designed
  // overlaps visible, so same-bay peers are excluded like group peers.
  const bayPeers = new Map();
  for (const o of roomData.objects) {
    if (!o?.bayId || !o.id) continue;
    if (!bayPeers.has(o.bayId)) bayPeers.set(o.bayId, []);
    bayPeers.get(o.bayId).push(o.id);
  }
  const all = [];
  for (const obj of roomData.objects) {
    if (!isReportedSource(obj)) continue;
    if (!obj.center || !obj.dimensions) continue;
    const sameBay = obj.bayId ? bayPeers.get(obj.bayId) : null;
    const result = checkPlacement(obj, rd, {
      excludeIds: sameBay ? [obj.id, ...sameBay] : [obj.id],
      excludeGroupIds: obj.fixtureGroupId ? [obj.fixtureGroupId] : [],
      skipChecks: toSkipChecks(constraints),
      structureDoorsById,
    });
    const filtered = filterCollisions(
      result.collisions || [],
      (obj.type || '').toLowerCase(),
      constraints
    );
    for (const c of filtered) {
      all.push({
        ...c,
        sourceId: obj.id,
        sourceType: obj.type,
        actionable: isActionable(obj),
      });
    }
  }

  // Intra-vanity accessory overlaps (mirror-mirror / overhead-overhead /
  // sconce-on-mirror within one vanity). These are silenced by the loop above
  // (same-group pairs are excluded), but fix-all CAN now resolve them by
  // regenerating the vanity's mirror + lights — so surface them here to keep
  // detection == fixability.
  for (const c of checkIntraGroupAccessoryCollisions(roomData.objects)) {
    all.push({
      type: 'intra-vanity',
      sourceId: c.a.id,
      sourceType: c.a.type,
      target: { id: c.b.id, type: c.b.type },
      reason: c.reason,
    });
  }

  return dedupeCollisions(all);
}

/**
 * Ids "Fix all" may hand to the bathroom pipeline. Reported-only sources
 * (kitchen appliances) are excluded so an appliance never reaches a
 * resolver that cannot move it; their red highlight still comes from
 * `collidingObjectIds`.
 */
export function fixtureIdsFromCollisions(collisions, roomData) {
  const byId = new Map(
    (roomData?.objects || []).filter((o) => o?.id).map((o) => [o.id, o])
  );
  const targetOk = (id) => {
    // Targets carry only { id, type } — resolve the object to apply the
    // same ownership gate the source side gets (#1532 step 1). Without the
    // room (legacy callers), keep the old inclusive behaviour.
    if (!byId.size) return true;
    const o = byId.get(id);
    return !o || isActionable(o);
  };
  const ids = new Set();
  for (const c of collisions) {
    if (c.actionable === false) continue;
    if (c.sourceId) ids.add(c.sourceId);
    if (c.target?.id && targetOk(c.target.id)) ids.add(c.target.id);
  }
  return [...ids];
}

/**
 * Object IDs that should render with the collision indicator (red).
 * Propagates across fixtureGroupId: if any actionable member of a group
 * collides, every member of that group is included, so the whole
 * vanity/tub assembly lights up together.
 */
export function collidingObjectIds(roomData, structureData) {
  const collisions = detectRoomCollisions(roomData, structureData);
  if (collisions.length === 0) return new Set();

  const direct = new Set();
  for (const c of collisions) {
    if (c.sourceId) direct.add(c.sourceId);
    // Target is the other party for fixture-to-fixture / fixture-clearance.
    // For door-swing / wall-bounds, target.id refers to a door/wall — not an
    // object — so it harmlessly won't match anything in roomData.objects.
    if (c.target?.id) direct.add(c.target.id);
  }

  const collidingGroupIds = new Set();
  const objects = roomData?.objects || [];
  for (const obj of objects) {
    if (direct.has(obj.id) && obj.fixtureGroupId) {
      collidingGroupIds.add(obj.fixtureGroupId);
    }
  }

  const result = new Set(direct);
  for (const obj of objects) {
    if (obj.fixtureGroupId && collidingGroupIds.has(obj.fixtureGroupId)) {
      result.add(obj.id);
    }
  }
  return result;
}
